import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  Clock,
  Coins,
  Crosshair,
  FileText,
  ListOrdered,
  MapPin,
  Minus,
  Monitor,
  MoreHorizontal,
  Navigation,
  Play,
  Plus,
  Route,
  Smartphone,
} from 'lucide-react';
import {
  BACKUP_SUMMARY,
  COIN_SUMMARY,
  DEVICES,
  FEATURE_CARDS,
  RECENT_ITEMS,
  STAT_CARDS,
  TIP_CARDS,
  USAGE,
  type RecentItem,
  type RecentKind,
  type UsagePeriod,
} from '../../data/dashboard-mock';
import './dashboard.css';

type RecentTab = 'all' | RecentKind;
type MapStyle = 'map' | 'satellite' | 'terrain';

const TABS: { id: RecentTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'stationary', label: 'Stationary' },
  { id: 'playlist', label: 'Playlists' },
  { id: 'itinerary', label: 'Itineraries' },
  { id: 'article', label: 'Articles' },
];

const KIND_ICON: Record<RecentKind, React.ReactNode> = {
  stationary: <MapPin size={16} />,
  playlist: <ListOrdered size={16} />,
  itinerary: <Route size={16} />,
  article: <FileText size={16} />,
};

export interface DashboardViewProps {
  fullName: string;
  email: string;
  searchQuery: string;
  loading?: boolean;
}

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function usePreviewLoading(enabled: boolean): boolean {
  const [loading, setLoading] = useState(() => enabled && !prefersReducedMotion());

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    if (prefersReducedMotion()) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const id = window.setTimeout(() => setLoading(false), 680);
    return () => window.clearTimeout(id);
  }, [enabled]);

  return loading;
}

function initial(fullName: string): string {
  const trimmed = fullName.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : 'M';
}

function Skel({ className = '' }: { className?: string }) {
  return <span className={`dash-skel ${className}`} />;
}

function HeroArt() {
  return (
    <svg className="dash-hero__svg" viewBox="0 0 460 230" aria-hidden="true">
      <ellipse cx="390" cy="42" rx="26" ry="26" fill="var(--ml-accent-soft, #ffe08a)" />
      <ellipse cx="70" cy="58" rx="22" ry="14" fill="#fff" opacity="0.75" />
      <ellipse cx="98" cy="52" rx="16" ry="12" fill="#fff" opacity="0.8" />
      <path d="M0 150c40-28 70-8 110-24 36-14 54 10 92-6 34-14 60 8 96-10 28-14 48 2 78-12 20-8 36 4 84-6v138H0z" fill="#b7e7c2" />
      <path d="M0 176c50-20 80 12 130-4 42-14 70 16 120-6 40-16 70 8 110-8 24-10 40 2 100-10v82H0z" fill="#8fd39a" />
      <path d="M40 168c30 18 48 8 70 20 18 10 30-6 52 8 20 12 36 4 58 16" fill="none" stroke="#7ec8e3" strokeWidth="10" strokeLinecap="round" />
      <path d="M70 188c40-36 78-20 112-48 28-22 58-18 96 4 24 14 48 10 78-6" fill="none" stroke="var(--ml-primary, #6d4dff)" strokeWidth="5" strokeLinecap="round" strokeDasharray="2 8" />
      <g>
        <path d="M118 132c-10 16-18 26-18 36a18 18 0 0 0 36 0c0-10-8-20-18-36z" fill="var(--ml-danger, #ef4444)" />
        <circle cx="118" cy="166" r="5" fill="#fff" />
      </g>
      <g>
        <path d="M196 108c-10 16-18 26-18 36a18 18 0 0 0 36 0c0-10-8-20-18-36z" fill="var(--ml-info, #3b82f6)" />
        <circle cx="196" cy="142" r="5" fill="#fff" />
      </g>
      <g>
        <path d="M286 96c-12 18-20 28-20 40a20 20 0 0 0 40 0c0-12-8-22-20-40z" fill="var(--ml-primary, #7c5cff)" />
        <circle cx="286" cy="132" r="6" fill="#fff" />
      </g>
      <g>
        <path d="M352 124c-10 16-18 26-18 36a18 18 0 0 0 36 0c0-10-8-20-18-36z" fill="var(--ml-success, #22c55e)" />
        <circle cx="352" cy="158" r="5" fill="#fff" />
      </g>
      <circle cx="150" cy="176" r="10" fill="#3f8f55" />
      <rect x="146" y="176" width="8" height="14" fill="#2f6b40" />
      <rect x="230" y="168" width="22" height="16" rx="2" fill="#f4d7b0" />
      <path d="M226 168l15-12 15 12" fill="#e07a4a" />
    </svg>
  );
}

function Hero() {
  return (
    <section className="dash-hero">
      <div className="dash-hero__copy">
        <h2>
          Explore anywhere,
          <br />
          your way.
        </h2>
        <p>Create, play and manage mock locations with powerful tools.</p>
      </div>
      <div className="dash-hero__art">
        <HeroArt />
      </div>
    </section>
  );
}

function HeroSkeleton() {
  return (
    <div className="dash-hero dash-hero--skeleton" data-testid="skeleton-hero" aria-hidden="true">
      <Skel className="dash-skel--title" />
      <Skel className="dash-skel--line" />
      <Skel className="dash-skel--art" />
    </div>
  );
}

function FeatureGrid() {
  return (
    <div className="dash-features">
      {FEATURE_CARDS.map((card) => (
        <Link key={card.id} to={card.to} className={`dash-feature dash-feature--${card.id}`}>
          <span className={`dash-feature__icon dash-kind dash-kind--${card.id}`} aria-hidden="true">
            {KIND_ICON[card.id]}
          </span>
          <strong>{card.title}</strong>
          <p>{card.text}</p>
          <span className="dash-feature__count">
            {card.count}
            <ChevronRight size={16} aria-hidden="true" />
          </span>
        </Link>
      ))}
    </div>
  );
}

function FeatureSkeleton() {
  return (
    <div className="dash-features" data-testid="skeleton-features" aria-hidden="true">
      {FEATURE_CARDS.map((card) => (
        <div key={card.id} className="dash-feature dash-feature--skeleton">
          <Skel className="dash-skel--icon" />
          <Skel className="dash-skel--line" />
          <Skel className="dash-skel--short" />
        </div>
      ))}
    </div>
  );
}

const STAT_ICON: Record<string, React.ReactNode> = {
  coins: <Coins size={18} />,
  devices: <Smartphone size={18} />,
  sessions: <Play size={18} />,
  locations: <Navigation size={18} />,
};

function StatRow() {
  return (
    <div className="dash-stats">
      {STAT_CARDS.map((card) => (
        <Link key={card.id} to={card.to} className="dash-stat">
          <span className={`dash-stat__icon dash-stat__icon--${card.id}`} aria-hidden="true">
            {STAT_ICON[card.id]}
          </span>
          <span className="dash-stat__copy">
            <strong>{card.value}</strong>
            <span>{card.label}</span>
          </span>
          <ChevronRight size={16} aria-hidden="true" />
        </Link>
      ))}
    </div>
  );
}

function StatSkeleton() {
  return (
    <div className="dash-stats" data-testid="skeleton-stats" aria-hidden="true">
      {STAT_CARDS.map((card) => (
        <div key={card.id} className="dash-stat dash-stat--skeleton">
          <Skel className="dash-skel--icon" />
          <Skel className="dash-skel--line" />
        </div>
      ))}
    </div>
  );
}

function RecentCard({ items }: { items: RecentItem[] }) {
  const [tab, setTab] = useState<RecentTab>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const visible = tab === 'all' ? items : items.filter((item) => item.kind === tab);

  useEffect(() => {
    if (!openId) return undefined;
    const close = () => setOpenId(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openId]);

  return (
    <section className="dash-card dash-recent" aria-labelledby="recent-heading">
      <div className="dash-card__head">
        <h2 id="recent-heading">Recent Items</h2>
        <button type="button" className="dash-text-btn" onClick={() => setTab('all')}>
          View All
        </button>
      </div>
      <div className="dash-tabs" role="tablist" aria-label="Recent item types">
        {TABS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`recent-tab-${item.id}`}
            aria-selected={tab === item.id}
            aria-controls="recent-panel"
            className={tab === item.id ? 'is-selected' : ''}
            onClick={() => setTab(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id="recent-panel"
        aria-labelledby={`recent-tab-${tab}`}
        className="dash-recent__list"
      >
        {visible.length === 0 && (
          <div className="dash-recent__empty">
            <MapPin size={22} aria-hidden="true" />
            <p>{items.length === 0 ? 'No matches for this search.' : 'No items in this view.'}</p>
          </div>
        )}
        {visible.map((item) => (
          <div key={item.id} className="dash-recent__row">
            <Link to={item.to} className="dash-recent__main">
              <span className={`dash-kind dash-kind--${item.kind}`} aria-hidden="true">
                {KIND_ICON[item.kind]}
              </span>
              <span>
                <strong>{item.name}</strong>
                <small>{item.kindLabel}</small>
              </span>
            </Link>
            <span className="dash-recent__time">{item.ago}</span>
            <div className="dash-recent__more">
              <button
                type="button"
                aria-label={`Actions for ${item.name}`}
                aria-expanded={openId === item.id}
                onMouseDown={(event) => event.stopPropagation()}
                onClick={() => setOpenId((current) => (current === item.id ? null : item.id))}
              >
                <MoreHorizontal size={16} aria-hidden="true" />
              </button>
              {openId === item.id && (
                <div className="dash-recent__menu" onMouseDown={(event) => event.stopPropagation()}>
                  <Link to={item.to}>Open</Link>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RecentSkeleton() {
  return (
    <div className="dash-card" data-testid="skeleton-recent" aria-hidden="true">
      <Skel className="dash-skel--line" />
      <div className="dash-recent__list">
        {[0, 1, 2, 3].map((key) => (
          <Skel key={key} className="dash-skel--row" />
        ))}
      </div>
    </div>
  );
}

function MapArt() {
  return (
    <svg className="dash-map__svg" viewBox="0 0 520 340" aria-hidden="true">
      <rect width="520" height="340" fill="transparent" />
      <path d="M40 70c40 20 30 50 70 40s40-40 80-10 50 30 90 10 60-30 90 0 40 40 80 20" fill="none" stroke="currentColor" strokeWidth="10" opacity="0.18" />
      <path d="M20 180c60-20 80 40 140 10s70-50 120-10 80 30 140-10" fill="none" stroke="currentColor" strokeWidth="8" opacity="0.14" />
      <path d="M80 260c50 20 70-30 120-10s60 40 110 10 70-20 120 10" fill="none" stroke="currentColor" strokeWidth="8" opacity="0.12" />
      <ellipse cx="120" cy="210" rx="36" ry="18" fill="#86efac" opacity="0.55" />
      <ellipse cx="400" cy="120" rx="28" ry="16" fill="#7dd3fc" opacity="0.45" />
      <path d="M110 168c50-20 90 10 140 4 46-6 70 24 120 8 40-12 70-6 110 16" fill="none" stroke="var(--ml-primary, #7c5cff)" strokeWidth="5" strokeLinecap="round" />
      <g>
        <path d="M108 132c-8 12-14 20-14 28a14 14 0 0 0 28 0c0-8-6-16-14-28z" fill="var(--ml-primary-soft, #a78bfa)" />
        <circle cx="108" cy="158" r="4" fill="#fff" />
      </g>
      <g>
        <path d="M168 150c-8 12-14 20-14 28a14 14 0 0 0 28 0c0-8-6-16-14-28z" fill="var(--ml-danger, #ef4444)" />
        <circle cx="168" cy="176" r="4" fill="#fff" />
      </g>
      <g>
        <path d="M250 156c-8 12-14 20-14 28a14 14 0 0 0 28 0c0-8-6-16-14-28z" fill="var(--ml-info, #3b82f6)" />
        <circle cx="250" cy="182" r="4" fill="#fff" />
      </g>
      <g>
        <path d="M360 128c-8 12-14 20-14 28a14 14 0 0 0 28 0c0-8-6-16-14-28z" fill="var(--ml-success, #22c55e)" />
        <circle cx="360" cy="154" r="4" fill="#fff" />
      </g>
      <g>
        <path d="M430 168c-8 12-14 20-14 28a14 14 0 0 0 28 0c0-8-6-16-14-28z" fill="var(--ml-warning, #f59e0b)" />
        <circle cx="430" cy="194" r="4" fill="#fff" />
      </g>
      <text x="92" y="78">Rohini</text>
      <text x="78" y="214">Janakpuri</text>
      <text x="48" y="286">Dwarka</text>
      <text x="214" y="214">New Delhi</text>
      <text x="400" y="214">Noida</text>
      <text x="150" y="300" className="dash-map__small">Indira Gandhi International Airport</text>
    </svg>
  );
}

function MapCard() {
  const [style, setStyle] = useState<MapStyle>('map');
  const [zoom, setZoom] = useState(1);
  const [opened, setOpened] = useState(false);

  const zoomBy = (delta: number) => {
    setZoom((current) => {
      const next = Math.round((current + delta) * 10) / 10;
      return Math.min(1.6, Math.max(1, next));
    });
  };

  return (
    <section className={`dash-card dash-map${opened ? ' is-open' : ''}`} aria-labelledby="map-heading">
      <div className="dash-card__head">
        <h2 id="map-heading">Your Quick Map Preview</h2>
        <button
          type="button"
          className="dash-text-btn"
          aria-pressed={opened}
          onClick={() => {
            setOpened((current) => !current);
            setStyle('map');
            setZoom((current) => (current > 1 ? 1 : 1.3));
          }}
        >
          <MapPin size={14} aria-hidden="true" />
          Open in Map
        </button>
      </div>
      <div className={`dash-map__stage is-${style}`}>
        <div className="dash-map__canvas" style={{ transform: `scale(${zoom})` }}>
          <MapArt />
        </div>
        <div className="dash-map__zoom">
          <button type="button" aria-label="Zoom in" onClick={() => zoomBy(0.2)} disabled={zoom >= 1.6}>
            <Plus size={14} aria-hidden="true" />
          </button>
          <button type="button" aria-label="Zoom out" onClick={() => zoomBy(-0.2)} disabled={zoom <= 1}>
            <Minus size={14} aria-hidden="true" />
          </button>
          <button
            type="button"
            aria-label="Reset map view"
            onClick={() => {
              setZoom(1);
              setOpened(false);
            }}
          >
            <Crosshair size={14} aria-hidden="true" />
          </button>
        </div>
        <div className="dash-map__styles" role="group" aria-label="Map style">
          {(['map', 'satellite', 'terrain'] as MapStyle[]).map((item) => (
            <button
              key={item}
              type="button"
              aria-pressed={style === item}
              onClick={() => setStyle(item)}
            >
              {item === 'map' ? 'Map' : item === 'satellite' ? 'Satellite' : 'Terrain'}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function MapSkeleton() {
  return <div className="dash-card dash-map dash-map--skeleton" data-testid="skeleton-map" aria-hidden="true"><Skel className="dash-skel--map" /></div>;
}

function AccountCard({ fullName, email }: { fullName: string; email: string }) {
  return (
    <section className="dash-card dash-account" aria-labelledby="account-heading">
      <div className="dash-card__head">
        <h2 id="account-heading">Your Account</h2>
        <Link to="/app/account" className="dash-ghost-btn">
          Edit
        </Link>
      </div>
      <div className="dash-account__person">
        <span className="dash-account__avatar" aria-hidden="true">{initial(fullName)}</span>
        <span>
          <strong>{fullName}</strong>
          {email && <small>{email}</small>}
        </span>
      </div>
      <span className="dash-account__badge">Premium User</span>
    </section>
  );
}

function AccountSkeleton() {
  return (
    <div className="dash-card" data-testid="skeleton-account" aria-hidden="true">
      <Skel className="dash-skel--line" />
      <Skel className="dash-skel--row" />
    </div>
  );
}

function CoinsCard() {
  return (
    <section className="dash-card" aria-labelledby="coins-heading">
      <div className="dash-card__head">
        <h2 id="coins-heading">Mock Coins</h2>
        <Link to="/app/coin-usage" className="dash-buy">
          <Plus size={14} aria-hidden="true" />
          Buy Coins
        </Link>
      </div>
      <div className="dash-coins__balance">
        <span className="dash-stat__icon dash-stat__icon--coins" aria-hidden="true">
          <Coins size={18} />
        </span>
        <strong>{COIN_SUMMARY.balance}</strong>
      </div>
      <div className="dash-coins__split">
        <span>
          <small>Used (This Month)</small>
          <strong>{COIN_SUMMARY.used}</strong>
        </span>
        <span>
          <small>Earned (This Month)</small>
          <strong>{COIN_SUMMARY.earned}</strong>
        </span>
      </div>
    </section>
  );
}

function CoinsSkeleton() {
  return (
    <div className="dash-card" data-testid="skeleton-coins" aria-hidden="true">
      <Skel className="dash-skel--line" />
      <Skel className="dash-skel--title" />
      <Skel className="dash-skel--row" />
    </div>
  );
}

function AndroidMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="dash-os">
      <path fill="#3DDC84" d="M7 9h10a1 1 0 0 1 1 1v7.2A2.8 2.8 0 0 1 15.2 20H8.8A2.8 2.8 0 0 1 6 17.2V10a1 1 0 0 1 1-1z" />
      <circle cx="9.5" cy="13" r="0.8" fill="#073b22" />
      <circle cx="14.5" cy="13" r="0.8" fill="#073b22" />
      <path d="M9 8.6 8 6.2M15 8.6l1-2.4" fill="none" stroke="#3DDC84" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function DriveMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="dash-os">
      <path fill="#34A853" d="M8.2 20.2 3.2 12h6.2l4.8 8.2z" />
      <path fill="#FBBC04" d="M15.6 20.2 10.6 12h9.8l-4.8 8.2z" />
      <path fill="#4285F4" d="M12 12.2 8.2 4.2h7.6L12 12.2z" />
    </svg>
  );
}

function DevicesCard() {
  return (
    <section className="dash-card" aria-labelledby="devices-heading">
      <div className="dash-card__head">
        <h2 id="devices-heading">Connected Devices</h2>
        <Link to="/app/devices" className="dash-text-btn">Manage</Link>
      </div>
      <ul className="dash-device-list">
        {DEVICES.map((device) => (
          <li key={device.id}>
            <span className="dash-device__icon" aria-hidden="true">
              {device.platform === 'android' ? <AndroidMark /> : <Monitor size={18} />}
            </span>
            <span>
              <strong>{device.name}</strong>
              <small>{device.meta}</small>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DevicesSkeleton() {
  return (
    <div className="dash-card" data-testid="skeleton-devices" aria-hidden="true">
      <Skel className="dash-skel--line" />
      <Skel className="dash-skel--row" />
      <Skel className="dash-skel--row" />
    </div>
  );
}

function BackupCard({ email }: { email: string }) {
  return (
    <section className="dash-card" aria-labelledby="backup-heading">
      <div className="dash-card__head">
        <h2 id="backup-heading">Backup & Sync</h2>
        <span className="dash-pill">{BACKUP_SUMMARY.configured}</span>
      </div>
      <Link to="/app/drive-backup" className="dash-backup">
        <DriveMark />
        <span>
          <strong>{BACKUP_SUMMARY.provider}</strong>
          <small>{email || 'Signed-in account'}</small>
        </span>
        <em>{BACKUP_SUMMARY.status}</em>
        <ChevronRight size={16} aria-hidden="true" />
      </Link>
      <p className="dash-backup__meta">{BACKUP_SUMMARY.lastBackup}</p>
    </section>
  );
}

function BackupSkeleton() {
  return (
    <div className="dash-card" data-testid="skeleton-backup" aria-hidden="true">
      <Skel className="dash-skel--line" />
      <Skel className="dash-skel--row" />
    </div>
  );
}

function UsageCard() {
  const [period, setPeriod] = useState<UsagePeriod>('month');
  const snapshot = USAGE[period];
  const cells = [
    { icon: <Play size={14} />, value: String(snapshot.sessions), label: 'Sessions', tone: 'violet' },
    { icon: <Navigation size={14} />, value: snapshot.distance, label: 'Distance', tone: 'blue' },
    { icon: <Clock size={14} />, value: snapshot.time, label: 'Total Time', tone: 'green' },
    { icon: <MapPin size={14} />, value: String(snapshot.locations), label: 'Locations', tone: 'amber' },
  ];

  return (
    <section className="dash-card" aria-labelledby="usage-heading">
      <div className="dash-card__head">
        <h2 id="usage-heading">App Usage</h2>
        <select
          aria-label="Usage period"
          value={period}
          onChange={(event) => setPeriod(event.target.value as UsagePeriod)}
        >
          <option value="month">This Month</option>
          <option value="last">Last Month</option>
        </select>
      </div>
      <div className="dash-usage">
        {cells.map((cell) => (
          <div key={cell.label} className={`dash-usage__cell dash-usage__cell--${cell.tone}`}>
            <span aria-hidden="true">{cell.icon}</span>
            <strong>{cell.value}</strong>
            <small>{cell.label}</small>
          </div>
        ))}
      </div>
    </section>
  );
}

function UsageSkeleton() {
  return (
    <div className="dash-card" data-testid="skeleton-usage" aria-hidden="true">
      <Skel className="dash-skel--line" />
      <div className="dash-usage">
        {[0, 1, 2, 3].map((key) => (
          <Skel key={key} className="dash-skel--cell" />
        ))}
      </div>
    </div>
  );
}

function Tips() {
  return (
    <section className="dash-tips-wrap" aria-labelledby="tips-heading">
      <h2 id="tips-heading">Tips & Guides</h2>
      <div className="dash-tips">
        {TIP_CARDS.map((tip) => (
          <Link key={tip.id} to={tip.to} className={`dash-tip dash-tip--${tip.id}`}>
            <span>
              <strong>{tip.title}</strong>
              <small>{tip.text}</small>
            </span>
            <span className={`dash-kind dash-kind--${tip.id}`} aria-hidden="true">
              {KIND_ICON[tip.id]}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function TipsSkeleton() {
  return (
    <div className="dash-tips-wrap" data-testid="skeleton-tips" aria-hidden="true">
      <Skel className="dash-skel--line" />
      <div className="dash-tips">
        {[0, 1, 2, 3].map((key) => (
          <Skel key={key} className="dash-skel--tip" />
        ))}
      </div>
    </div>
  );
}

const DashboardView: React.FC<DashboardViewProps> = ({
  fullName,
  email,
  searchQuery,
  loading: loadingProp,
}) => {
  const autoLoading = usePreviewLoading(loadingProp === undefined);
  const loading = loadingProp ?? autoLoading;
  const query = searchQuery.trim().toLowerCase();
  const recent = query
    ? RECENT_ITEMS.filter((item) => `${item.name} ${item.kindLabel}`.toLowerCase().includes(query))
    : RECENT_ITEMS;

  return (
    <div className="dash" aria-busy={loading}>
      {loading && <div className="visually-hidden" role="status">Loading dashboard</div>}
      {loading ? <HeroSkeleton /> : <Hero />}
      {loading ? <FeatureSkeleton /> : <FeatureGrid />}
      {loading ? <StatSkeleton /> : <StatRow />}
      <aside className="dash-rail" aria-label="Account summary">
        {loading ? <AccountSkeleton /> : <AccountCard fullName={fullName} email={email} />}
        {loading ? <CoinsSkeleton /> : <CoinsCard />}
        {loading ? <DevicesSkeleton /> : <DevicesCard />}
        {loading ? <BackupSkeleton /> : <BackupCard email={email} />}
        {loading ? <UsageSkeleton /> : <UsageCard />}
      </aside>
      <div className="dash-split">
        {loading ? <RecentSkeleton /> : <RecentCard items={recent} />}
        {loading ? <MapSkeleton /> : <MapCard />}
      </div>
      {loading ? <TipsSkeleton /> : <Tips />}
    </div>
  );
};

export default DashboardView;
